/**
 * Shared, non-UI taxonomy for agent-connection model providers.
 *
 * Lives in `src/utils` so both stores (`@/stores/connectionStore`) and UI
 * helpers (`@/components/WorkflowDesigner/workflowHelpers`) can depend on it
 * without the store pulling in a component-layer module — which would invert
 * the dependency direction and risk circular imports.
 */

/**
 * Normalized identifiers for the model provider behind an agent connection.
 * Mirrors the values accepted by an Agent action's `agentModelType` parameter.
 */
export type AgentModelProvider =
  | 'AzureOpenAI'
  | 'OpenAI'
  | 'GitHubCopilot'
  | 'FoundryAgentService'
  | 'FoundryAgentServiceV2';

const KNOWN_AGENT_PROVIDERS: readonly AgentModelProvider[] = [
  'FoundryAgentServiceV2',
  'FoundryAgentService',
  'AzureOpenAI',
  'OpenAI',
  'GitHubCopilot',
];

/**
 * Resolve any free-form identifier (explicit provider name, endpoint URL, api id path segment, …) to its canonical `AgentModelProvider`.
 * This should not be run on resourceIds since they may contain substrings that match provider patterns incorrectly.
 * Returns `undefined` when nothing matches.
 *
 * Exact (case-insensitive) matches win; substring fallbacks handle path-shaped
 * inputs. FoundryV2 must be checked before V1 since `'foundryagentservice'`
 * is a prefix of both.
 */
export const normalizeProviderName = (value: string | undefined): AgentModelProvider | undefined => {
  if (!value) return undefined;
  const lower = value.toLowerCase();
  const exact = KNOWN_AGENT_PROVIDERS.find((p) => p.toLowerCase() === lower);
  if (exact) return exact;
  if (lower.includes('foundryagentservicev2')) return 'FoundryAgentServiceV2';
  if (lower.includes('/projects/')) return 'FoundryAgentService';
  if (lower.includes('.cognitiveservices.') || lower.includes('azureopenai')) return 'AzureOpenAI';
  if (lower.includes('.openai.azure.com')) return 'AzureOpenAI';
  if (lower.includes('.openai.')) return 'OpenAI';
  if (lower.includes('githubcopilot') || lower.includes('ghcp')) return 'GitHubCopilot';
  return undefined;
};

/** Agent providers backed by Azure AI Foundry. */
export const FOUNDRY_AGENT_PROVIDERS: ReadonlySet<AgentModelProvider> = new Set<AgentModelProvider>([
  'FoundryAgentService',
  'FoundryAgentServiceV2',
]);

/**
 * Agent providers backed by native model services for a *workflow* (native)
 * agent. GitHub Copilot is intentionally excluded here — GHCP is a coding-agent
 * feature and shouldn't be surfaced in the native agent connection picker.
 */
export const NATIVE_AGENT_PROVIDERS: ReadonlySet<AgentModelProvider> = new Set<AgentModelProvider>([
  'AzureOpenAI',
  'OpenAI',
]);

/**
 * Agent providers offered for a *coding* agent. Coding agents run on the GHCP
 * harness, so GitHub Copilot connections are valid here in addition to the
 * standard native model services.
 */
export const CODING_AGENT_PROVIDERS: ReadonlySet<AgentModelProvider> = new Set<AgentModelProvider>([
  'AzureOpenAI',
  'OpenAI',
  'GitHubCopilot',
]);

/**
 * The exact set of values the backend `AgentConnectionType` enum accepts —
 * see `Flow.Data.Common/Entities/Agent/AgentConnectionType.cs`. Anything
 * written to `agentConnections[*].type` in connections.json must collapse
 * to one of these or backend validation will reject the workflow.
 */
export type BackendAgentConnectionType =
  | 'Model'
  | 'FoundryAgentService'
  | 'APIMGenAIGateway'
  | 'GitHubCopilot';

/**
 * Translate our finer-grained client-side provider names to the backend
 * `AgentConnectionType` enum before persisting to `agentConnections[*].type`.
 */
export const toBackendAgentConnectionType = (
  clientType: string | undefined,
): BackendAgentConnectionType => {
  if (!clientType) return 'Model';
  const normalized = normalizeProviderName(clientType);
  if (normalized && FOUNDRY_AGENT_PROVIDERS.has(normalized)) return 'FoundryAgentService';
  if (clientType === 'APIMGenAIGateway') return 'APIMGenAIGateway';
  // GitHub Copilot is its own backend connection type: the runtime keys auth
  // brokerage and endpoint selection off `connection.Type == GitHubCopilot`,
  // so it must be persisted verbatim rather than collapsed to 'Model'.
  if (normalized === 'GitHubCopilot') return 'GitHubCopilot';
  return 'Model';
};
